import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  FaReact, FaNodeJs, FaHtml5, FaCss3Alt, FaGitAlt, FaDocker,
} from 'react-icons/fa';
import {
  SiJavascript, SiTypescript, SiDotnet, SiCsharp, SiMongodb,
  SiMysql, SiTailwindcss, SiRedux, SiFirebase, SiAzuredevops,
} from 'react-icons/si';
import { TbBrain } from 'react-icons/tb';
import { HiCode } from 'react-icons/hi';

const clusters = [
  {
    id: 'frontend',
    title: 'Frontend',
    accent: '#61DAFB',
    skills: [
      { name: 'React',       icon: FaReact,       color: '#61DAFB' },
      { name: 'JavaScript',  icon: SiJavascript,  color: '#F7DF1E' },
      { name: 'TypeScript',  icon: SiTypescript,  color: '#3178C6' },
      { name: 'HTML5',       icon: FaHtml5,       color: '#E34F26' },
      { name: 'CSS3',        icon: FaCss3Alt,     color: '#1572B6' },
      { name: 'Tailwind',    icon: SiTailwindcss, color: '#06B6D4' },
      { name: 'Redux',       icon: SiRedux,       color: '#764ABC' },
    ],
  },
  {
    id: 'backend',
    title: 'Backend',
    accent: '#a78bfa',
    skills: [
      { name: '.NET',    icon: SiDotnet,  color: '#512BD4' },
      { name: 'C#',      icon: SiCsharp,  color: '#239120' },
      { name: 'Node.js', icon: FaNodeJs,  color: '#339933' },
    ],
  },
  {
    id: 'database',
    title: 'Database',
    accent: '#6fe068',
    skills: [
      { name: 'MongoDB',  icon: SiMongodb,  color: '#47A248' },
      { name: 'MySQL',    icon: SiMysql,    color: '#4479A1' },
      { name: 'Firebase', icon: SiFirebase, color: '#FFCA28' },
    ],
  },
  {
    id: 'devops',
    title: 'Cloud & DevOps',
    accent: '#60a5fa',
    skills: [
      { name: 'Azure',  icon: SiAzuredevops, color: '#0078D4' },
      { name: 'Docker', icon: FaDocker,       color: '#2496ED' },
      { name: 'Git',    icon: FaGitAlt,       color: '#F05032' },
      { name: 'CI/CD',  icon: HiCode,         color: '#00f2ff' },
    ],
  },
  {
    id: 'ai',
    title: 'AI & Modern',
    accent: '#f0abfc',
    skills: [
      { name: 'LLM APIs',     icon: TbBrain, color: '#f0abfc' },
      { name: 'AI Agents',    icon: TbBrain, color: '#f0abfc' },
      { name: 'AI Integration', icon: TbBrain, color: '#f0abfc' },
    ],
  },
];

const containerVariant = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const pillVariant = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: 'easeOut' } },
};

function SkillPill({ skill }) {
  const [hovered, setHovered] = useState(false);
  const Icon = skill.icon;
  return (
    <motion.div
      variants={pillVariant}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.35rem 0.85rem',
        background: hovered ? `${skill.color}15` : 'rgba(25,32,41,0.8)',
        border: `1px solid ${hovered ? skill.color + '40' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: '9999px',
        cursor: 'default',
        transition: 'all 0.2s ease',
        boxShadow: hovered ? `0 0 12px ${skill.color}25` : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={14} color={hovered ? skill.color : '#849495'} style={{ transition: 'color 0.2s', flexShrink: 0 }} />
      <span style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '0.7rem',
        fontWeight: 500,
        letterSpacing: '0.04em',
        color: hovered ? '#dce3f0' : '#849495',
        transition: 'color 0.2s',
      }}>
        {skill.name}
      </span>
    </motion.div>
  );
}

function ClusterCard({ cluster, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(25,32,41,0.9)' : 'rgba(13,20,29,0.7)',
        border: `1px solid ${hovered ? cluster.accent + '25' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '16px',
        padding: '1.5rem',
        backdropFilter: 'blur(12px)',
        transition: 'all 0.3s ease',
        boxShadow: hovered ? `0 20px 60px rgba(0,0,0,0.4), 0 0 20px ${cluster.accent}08` : 'none',
      }}
    >
      {/* Cluster header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: cluster.accent,
          boxShadow: `0 0 10px ${cluster.accent}`,
          flexShrink: 0,
        }} />
        <span style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: cluster.accent,
        }}>
          {cluster.title}
        </span>
      </div>

      {/* Skills pills */}
      <motion.div
        variants={containerVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
      >
        {cluster.skills.map((skill) => (
          <SkillPill key={skill.name} skill={skill} />
        ))}
      </motion.div>
    </motion.div>
  );
}

export default function Skills() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="skills" className="section-padding overflow-hidden" style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(0,242,255,0.1), transparent)',
      }} />

      <div className="container-custom">
        {/* Header */}
        <div ref={ref} style={{ marginBottom: '4rem' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="section-label mb-4"
          >
            02 / STACK
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="heading-primary mb-4"
          >
            Full Stack <span className="text-gradient">Engineering</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ color: '#849495', maxWidth: '500px', lineHeight: 1.7 }}
          >
            Technologies I use to architect, build, and ship scalable digital products
            across the entire stack — from browser to cloud.
          </motion.p>
        </div>

        {/* ─── Center node + clusters layout ─── */}
        <div style={{ position: 'relative' }}>

          {/* Central hub */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              marginBottom: '3rem',
            }}
          >
            <div style={{
              position: 'relative',
              width: 140, height: 140,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(0,242,255,0.1) 0%, rgba(13,20,29,0.9) 70%)',
              border: '1px solid rgba(0,242,255,0.25)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 60px rgba(0,242,255,0.1)',
            }}>
              {/* Spinning ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute', inset: -10,
                  borderRadius: '50%',
                  border: '1px dashed rgba(0,242,255,0.15)',
                }}
              />
              <span className="text-mono" style={{
                color: '#00f2ff', fontSize: '0.6rem', letterSpacing: '0.12em',
                textAlign: 'center', lineHeight: 1.4,
              }}>
                FULL<br />STACK
              </span>
            </div>
          </motion.div>

          {/* Skill cluster grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1.25rem',
          }}>
            {clusters.map((cluster, i) => (
              <ClusterCard key={cluster.id} cluster={cluster} delay={0.35 + i * 0.1} />
            ))}
          </div>
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
      }} />
    </section>
  );
}
